import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BlobField from '../components/BlobField';
import LogoMark from '../components/LogoMark';

/**
 * AdminLayout — chrome shared by every /admin/* page.
 * Left sidebar (5 sections) + top bar with back-to-app + sign-out.
 * Access is gated by <AdminRoute> in App.js.
 */
const SECTIONS = [
  { key: 'overview',      to: '/admin',                label: 'Overview',      icon: '📊' },
  { key: 'users',         to: '/admin/users',          label: 'Users',         icon: '👥' },
  { key: 'transactions',  to: '/admin/transactions',   label: 'Transactions',  icon: '💱' },
  { key: 'deposits',      to: '/admin/deposits',       label: 'Deposits',      icon: '💰' },
  { key: 'withdrawals',   to: '/admin/withdrawals',    label: 'Withdrawals',   icon: '🏧' },
  { key: 'verifications', to: '/admin/verifications',  label: 'Verification',  icon: '✔️' },
  { key: 'settings',      to: '/admin/settings',       label: 'Settings',      icon: '⚙️' },
  { key: 'categories',    to: '/admin/categories',     label: 'Categories',    icon: '🏷️' },
  { key: 'referrals',     to: '/admin/referrals',      label: 'Referrals',     icon: '🎁' },
  { key: 'seed',          to: '/admin/seed',           label: 'Seed Network',  icon: '🌱' },
  { key: 'waitlist',      to: '/admin/waitlist',       label: 'Waitlist',      icon: '🎟️' },
  { key: 'creator-waitlist', to: '/admin/creator-waitlist', label: 'Creator Waitlist', icon: '🎨' },
  { key: 'launches',      to: '/admin/launches',       label: 'Launches',      icon: '🚀' },
  { key: 'demo',          to: '/admin/demo',           label: 'Demo Sim',      icon: '🎬' },
];

export default function AdminLayout({ children, active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="admin-shell">
      <BlobField />

      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="admin-brand">
          <LogoMark size={34} />
          <div>
            <div className="admin-brand-name">Clanster</div>
            <div className="admin-brand-role">Admin panel</div>
          </div>
        </div>

        <nav className="admin-nav">
          {SECTIONS.map((s) => (
            s.disabled ? (
              <div key={s.key} className="admin-nav-link disabled" data-testid={`admin-nav-${s.key}`}>
                <span className="admin-nav-icon">{s.icon}</span>
                <span className="admin-nav-label">{s.label}</span>
                {s.badge && <span className="admin-nav-badge">{s.badge}</span>}
              </div>
            ) : (
              <NavLink
                key={s.key}
                to={s.to}
                end={s.to === '/admin'}
                className={({ isActive }) => `admin-nav-link${(isActive || active === s.key) ? ' active' : ''}`}
                data-testid={`admin-nav-${s.key}`}
              >
                <span className="admin-nav-icon">{s.icon}</span>
                <span className="admin-nav-label">{s.label}</span>
              </NavLink>
            )
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-chip">
            <div className="admin-user-avatar">
              {(user?.display_name || 'A').split(' ').map(w => w[0]).slice(0, 2).join('')}
            </div>
            <div>
              <div className="admin-user-name">{user?.display_name || 'Admin'}</div>
              <div className="admin-user-role">Administrator</div>
            </div>
          </div>
          <button
            type="button"
            className="admin-back-btn"
            onClick={() => navigate('/home')}
            data-testid="admin-back-to-app"
          >
            ← Back to app
          </button>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
            data-testid="admin-signout"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-main">{children}</main>
    </div>
  );
}
